import React, { useEffect } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";

import { orgIdSelector } from "../../../selectors/orgIdSelector";
import {
  errorMessagesSelector,
  graphDatesSelector,
  graphParamsSelector,
  graphEntriesSelector,
  intervalSelector,
  logdevsOverviewSelector,
} from "../../../selectors/logdevsSelectors";
import {
  clearErr,
  fetchLogdevsGraph,
  fetchLogdevsOverview,
  fetchLogdevsTable,
  receiveDateRange,
  receiveRelativeInterval,
  receiveRelativeStart,
} from "../../../redux/actions/Logdevs";

import { ErrorBanner } from "../../Components/ErrorBanner";
import ActionsBar from "./common/ActionsBar";
import { getErrorMsgElt } from "./common/helpers";
import DatepickerContainer from "./common/DatepickerContainer";
import ResourceTitle from "./common/ResourceTitle";
import RefreshButton from "./common/RefreshButton";
import GraphAndSide from "./GraphAndSide";
import LogdevTableContainer from "./LogdevTableContainer";

import "./common/ResourcesView.scss";

/*
Constants and helpers
*/

const GRAPH_TITLE = "Log Events";

/*
Component
*/

const LogdevView = (props) => {
  const {
    dispatchClearErr,
    dispatchFetchLogdevs,
    errorMessages,
    graphData,
    graphParams,
    orgId,
    overview,
    selectedInterval,
    windowEnd,
    windowStart,
  } = props;

  useEffect(() => {
    dispatchFetchLogdevs({
      selectedInterval,
      orgId,
    });
  }, [orgId, selectedInterval, dispatchFetchLogdevs]);

  const onClickRefresh = () => {
    dispatchFetchLogdevs({
      selectedInterval,
      orgId,
    });
  };

  return (
    <div className="resources-view">
      <div className="resources-view-content">
        <ErrorBanner
          message={getErrorMsgElt(errorMessages)}
          dismiss={dispatchClearErr}
        />

        <ActionsBar left={1}>
          <ResourceTitle titleStr="Logging Devices" />

          <DatepickerContainer
            intervalSelector={intervalSelector}
            itemIdSelector={orgIdSelector}
            receiveDateRange={receiveDateRange}
          />

          <RefreshButton onClick={onClickRefresh} />
        </ActionsBar>

        <GraphAndSide
          graphData={graphData}
          graphParams={graphParams}
          graphTitle={GRAPH_TITLE}
          overview={overview}
          windowEnd={windowEnd}
          windowStart={windowStart}
        />

        <div className="resources-view-section">
          <LogdevTableContainer />
        </div>
      </div>
    </div>
  );
};

LogdevView.propTypes = {
  dispatchClearErr: PropTypes.func.isRequired,
  dispatchFetchLogdevs: PropTypes.func.isRequired,
  errorMessages: PropTypes.arrayOf(PropTypes.string).isRequired,
  graphData: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  graphParams: PropTypes.PropTypes.shape({}).isRequired,
  orgId: PropTypes.string.isRequired,
  overview: PropTypes.shape({}).isRequired,
  selectedInterval: PropTypes.shape({}).isRequired,
  windowEnd: PropTypes.string.isRequired,
  windowStart: PropTypes.string.isRequired,
};

LogdevView.defaultProps = {};

const mapStateToProps = (state) => {
  const graphData = graphEntriesSelector(state);
  const graphParams = graphParamsSelector(state);

  const errorMessages = errorMessagesSelector(state);

  const orgId = orgIdSelector(state);

  const overview = logdevsOverviewSelector(state);

  const selectedInterval = intervalSelector(state);

  const { windowEnd = "", windowStart = "" } = graphDatesSelector(state);

  return {
    errorMessages,
    graphData,
    graphParams,
    orgId,
    overview,
    selectedInterval,
    windowEnd,
    windowStart,
  };
};

const mapDispatchToProps = (dispatch) => ({
  dispatchClearErr: () => {
    dispatch(clearErr);
  },

  dispatchReceiveRelativeInterval: ({ orgId, relativeEnd, relativeStart }) => {
    dispatch(
      receiveRelativeInterval({
        orgId,
        relativeEnd,
        relativeStart,
      })
    );
  },

  dispatchReceiveRelativeStart: ({ relativeStart, orgId }) => {
    dispatch(
      receiveRelativeStart({
        orgId,
        relativeStart,
      })
    );
  },

  dispatchFetchLogdevs: ({ selectedInterval, orgId }) => {
    dispatch(
      fetchLogdevsGraph({
        insertId: orgId,
        selectedInterval,
        orgId,
      })
    );

    dispatch(
      fetchLogdevsTable({
        insertId: orgId,
        selectedInterval,
        orgId,
      })
    );

    dispatch(fetchLogdevsOverview(orgId));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(LogdevView);
